import React, { createContext, ReactNode, useReducer, useContext } from 'react';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import { merge } from 'lodash-es';
import moment from 'moment-timezone';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import { DateContext } from '../Measures';
import { Context } from '../../context';
import { MEASURE_TYPE } from '../MeasuresContext/utils/constants';
import {
  SET_VITALS_VALUES,
  SET_UNITS_VALUES,
  // not used
  SET_COMMENTS_VALUES,
  SET_DISABLE_VITALS,
  SET_LOADER,
  SET_ERRORS,
  SET_MEASURE_FIELDS,
  RESET_VITALS_VALUES,
  RESET_UNITS_VALUES,
  RESET_COMMENTS_VALUES,
  RESET_VITALS_NAMES,
} from './constants';
import { IErrors, IFields, IValues } from './interfaces';

type VitalsContextProviderProps = {
  children: ReactNode;
};

type VitalsContext = {
  names: string[];
  unitsValues: IValues;
  commentsValues: any;
  vitalDate: any;
  errors: IErrors;
  values: IValues;
  disabled: boolean;
  isLoading: boolean;
  activeMeasuresFields: any;
  validate?: (fieldName: string) => any;
  handleSubmit?: () => Promise<void>;
  haveErrors?: (fieldError: IErrors) => boolean;
  dispatch?: any;
};

type VitalsContextState = {
  names: string[];
  unitsValues: IValues;
  commentsValues: any;
  vitalDate: any;
  errors: IErrors;
  values: IValues;
  disabled: boolean;
  isLoading: boolean;
  activeMeasuresFields: any;
};
const initialState = {
  names: [],
  unitsValues: {},
  commentsValues: {},
  errors: {},
  values: {},
  vitalDate: Date.now(),
  disabled: true,
  isLoading: false,
  activeMeasuresFields: {},
};

const initVitalsContext = {
  names: [],
  unitsValues: {},
  commentsValues: {},
  errors: {},
  values: {},
  vitalDate: Date.now(),
  disabled: true,
  isLoading: false,
  activeMeasuresFields: {},
};

export const VitalsContextReducer = (
  state: VitalsContextState,
  action: { type: string; payload?: any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_VITALS_VALUES:
      {
        const { name } = action.payload;
        newState.values[name] = action.payload;
        newState.names = [...newState.names, name];
        newState.disabled = false;
      }
      break;
    case SET_MEASURE_FIELDS:
      newState.activeMeasuresFields = action.payload;
      break;
    case SET_UNITS_VALUES:
      {
        const { name } = action.payload;
        if (action.payload.value === false && newState.unitsValues[name]) {
          delete newState.unitsValues[name];
        }
        newState.unitsValues[name] = action.payload;
        newState.names = [...newState.names, name];
        newState.disabled = false;
      }
      break;
    case SET_LOADER:
      newState.isLoading = action.payload;
      break;
    case SET_COMMENTS_VALUES:
      {
        const { name } = action.payload;
        newState.commentsValues[name] = action.payload;
        newState.names = [...newState.names, name];
        newState.disabled = false;
      }
      break;
    case SET_DISABLE_VITALS:
      newState.disabled = action.payload;
      break;
    case RESET_VITALS_VALUES:
      newState.values = {};
      break;
    case RESET_UNITS_VALUES:
      newState.unitsValues = {};
      break;
    case RESET_COMMENTS_VALUES:
      newState.commentsValues = {};
      break;
    case RESET_VITALS_NAMES:
      newState.names = [];
      break;
    case SET_ERRORS:
      {
        const { name, error } = action.payload;
        newState.errors[name] = error;
      }
      break;

    default:
      newState = state;
      break;
  }
  return newState;
};

export const VitalsContext = createContext<VitalsContext>(
  initVitalsContext as VitalsContext,
);
export const VitalsContextProvider = ({
  children,
}: VitalsContextProviderProps) => {
  const [
    {
      names,
      unitsValues,
      commentsValues,
      errors,
      values,
      vitalDate,
      disabled,
      isLoading,
      activeMeasuresFields,
    },
    dispatch,
  ] = useReducer(VitalsContextReducer, initialState);

  const { handleMeasureChange, patient, currentUser } = useContext(Context);
  const { appVisit } = useContext(GlobalContext);
  const { updateDate } = useContext(DateContext);

  const patientNumber = (patient as mumms.patient)?.patientNumber;
  const patientSiteId = patient?.siteId.toString();
  const patientId = (patient as mumms.patient)?.id;
  const personId = currentUser.isMumms ? null : currentUser.userId;

  const validate = (fieldName: string) => {
    let newError = '';

    if (
      activeMeasuresFields[fieldName] &&
      activeMeasuresFields[fieldName].validation
    ) {
      newError = activeMeasuresFields[fieldName].validation!.rule(
        values,
        fieldName,
        activeMeasuresFields[fieldName].validation!.args,
      );
    }
    dispatch({
      type: SET_ERRORS,
      payload: { name: fieldName, error: newError },
    });
  };

  const haveErrors = (fieldError: IErrors) => {
    if (fieldError && Object.keys(fieldError)) {
      const haveError: any[] = [];
      // eslint-disable-next-line array-callback-return
      Object.keys(fieldError).forEach((key: string) => {
        if (fieldError[key] && fieldError[key].length > 0) {
          haveError.push(true);
        }
      });
      const hasError = haveError.length > 1;
      return hasError;
    }
    return false;
  };

  const resetData = () => {
    dispatch({
      type: RESET_UNITS_VALUES,
    });
    dispatch({
      type: RESET_COMMENTS_VALUES,
    });
    dispatch({
      type: RESET_VITALS_VALUES,
    });
    dispatch({
      type: RESET_VITALS_NAMES,
    });
  };

  const validateForm = () => {
    const allErrors: any | IErrors = {};
    // eslint-disable-next-line array-callback-return
    Object.keys(activeMeasuresFields).map((measureName: string) => {
      allErrors[measureName] = validate(measureName);
    });
    const hasErrors = haveErrors(allErrors);

    // setState({ errors });
    return !hasErrors;
  };

  const submitForm = async (): Promise<boolean> => {
    type Score = mumms.measure;

    const newValue = {
      ...values,
    };
    const newUnitsValues = {
      ...unitsValues,
    };
    const newCommentsValues = {
      ...commentsValues,
    };
    const newState = merge(newValue, newUnitsValues, newCommentsValues);
    const timezone = moment.tz.guess();
    const postdate = formatPostDate(updateDate);
    const measures: any = [];
    const measuretype = localStorage.getItem(MEASURE_TYPE);

    try {
      // TODO: UPDATE THIS TO THE BULK ENDPOINT CALL INSTEAD OF A LOOP
      Object.keys(newState).forEach((measure: any) => {
        const measureName = newState[measure].name;
        const measureValue = newState[measure].value || '';
        const commentsValue = newState[measure].commentsValue || '';
        const unitsValue =
          newState[measure].unitsValue || newState[measure].defaultUnit || '';

        // @note VISIT if a visit is being recorded save date and id
        const patientvisitId = appVisit ? appVisit.id : null;
        const visitdatetime = appVisit ? appVisit.effectivedatetime : null;

        const measureData = {
          comments: commentsValue as string,
          id: '',
          measurecategoryshortname: '',
          measuredate: '',
          measuredatetime: postdate as string,
          measuredatetimeshifted: '',
          measuretypeid: '',
          measuretypename: '',
          measuretypeshortname: measureName,
          patientid: patientId.toString() as string,
          patientnumber: patientNumber,
          personcredentials: '',
          personfirstname: '',
          personid: personId as string,
          personlastname: '',
          personnumber: '',
          siteid: patientSiteId as string,
          timezone,
          units: unitsValue,
          value: measureValue as string,
          patientvisit_id: patientvisitId,
          visitdatetime,
        };
        if (measureData.value !== '') {
          measures.push(measureData);
        }
      });

      await handleMeasureChange(
        patientNumber,
        measures,
        'POST',
        measuretype as string,
      )
        .then((e) => {
          resetData();
        })
        .catch((e: any) => {
          // eslint-disable-next-line no-shadow

          dispatch({
            type: SET_ERRORS,
            payload: { name: new Date().toString(), error: e },
          });
        });

      dispatch({
        type: SET_LOADER,
        payload: false,
      });

      return true;
      // TODO: Add response validation
    } catch (ex) {
      return false;
    }
  };

  const handleSubmit = async () => {
    dispatch({
      type: SET_LOADER,
      payload: true,
    });
    dispatch({
      type: SET_DISABLE_VITALS,
      payload: true,
    });

    if (validateForm()) {
      await submitForm();
      // INFO: this submit has a delay right now because
      // we are making a post call for each measure instead of bulk
      // this.props.handleCancel();

      // todo time for the success message to show in the pop up, disabled here as active i the graph
      // this.props.handleErrorMsg();

      // reset the state after submit
      // this.setState({
      //   values: {},
      //   unitsValues: {},
      //   commentsValues: {},
      //   errors: {},
      //   disabled: true,
      //   isLoading: false,
      //   names: [],
      // });
    }
  };

  return (
    <VitalsContext.Provider
      value={{
        names,
        unitsValues,
        commentsValues,
        errors,
        values,
        vitalDate,
        disabled,
        isLoading,
        activeMeasuresFields,
        validate,
        handleSubmit,
        haveErrors,
        dispatch,
      }}
    >
      {children}
    </VitalsContext.Provider>
  );
};

export default VitalsContextProvider;
